import {useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'

import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import LanguageIcon from '@mui/icons-material/Language'
import {
  Box,
  CircularProgress,
  keyframes,
  MenuItem,
  Select,
  Stack,
  Theme,
  useTheme,
} from '@mui/material'
import {makeStyles} from 'tss-react/mui'

import CustomBadge from '@progyny/components/Navigation/CustomBadge'
import useChangeLanguage from '@progyny/components/Navigation/useChangeLanguage'
import {useMeQuery} from '@progyny/generated/graphql'
import {useLanguageCriteria} from '@progyny/store/languageStore'
import {useMeStore} from '@progyny/store/meStore'

const LanguageSelector = ({onWhite}: {onWhite?: boolean}) => {
  const {classes} = useStyles()
  const {i18n} = useTranslation()
  const {data: meQuery} = useMeQuery()
  const {changeLanguage} = useChangeLanguage()
  const {isLanguageUpdated} = useLanguageCriteria()
  const theme = useTheme()
  const isLoggedIn = useMeStore((s) => s.isLoggedIn)

  const [language, setLanguage] = useState(
    localStorage.getItem('ui_language') || meQuery?.me?.member.language || 'en'
  )
  const [dismissHighlight, setDismissHighlight] = useState(false)
  const [loading, setLoading] = useState(false)

  const hideHighlight =
    dismissHighlight || !meQuery?.me?.member.highlightLanguages

  useEffect(() => {
    const lsVal = localStorage.getItem('ui_language')
    const meQueryMemberLang = meQuery?.me?.member.language ?? null
    const i18nLang = i18n.language || null
    const ui_language = i18nLang || lsVal || meQueryMemberLang || 'en'
    const didOverwrite = language !== ui_language
    // If language changes via hook, keeps the language selector in sync. Prefer i18n.language so we don't overwrite with stale localStorage when routes resync races.
    if (didOverwrite) {
      setLanguage(ui_language)
    }
  }, [isLanguageUpdated, language, meQuery?.me?.member.language, i18n.language])

  return (
    <Stack flexDirection={'row'} alignItems={'center'} gap={'4px'}>
      <CustomBadge
        badgeContent={<Trans i18nKey='navigation_language_selector_new' />}
        invisible={hideHighlight}
      >
        <Box className={classes.glowingBox}>
          <Box className={classes.glowingInner}>
            {loading ? (
              <CircularProgress
                size={24}
                className={classes.languageIcon}
                sx={{color: 'inherit'}}
              />
            ) : (
              <LanguageIcon className={classes.languageIcon} />
            )}
            <Select
              value={language}
              variant='standard'
              disableUnderline
              disabled={loading}
              onChange={async (event) => {
                const selectedValue = event.target.value
                setLoading(true)
                try {
                  if (isLoggedIn) {
                    await changeLanguage(selectedValue)
                  } else {
                    await changeLanguage(selectedValue, true)
                  }
                  setLanguage(selectedValue)
                } catch {
                  // Keep current language state when translations fail to load
                } finally {
                  setLoading(false)
                }
              }}
              autoWidth
              sx={{
                fontSize: '1rem',
                fontWeight: 500,
                color: theme.palette.primary.main,
                '& .MuiSelect-icon': {
                  color: theme.palette.primary.main,
                },
              }}
              onFocus={() => setDismissHighlight(true)}
              IconComponent={KeyboardArrowDownOutlinedIcon}
            >
              {meQuery?.languages.map((lang) => (
                <MenuItem
                  key={lang?.code}
                  sx={{fontSize: '1rem', fontWeight: 500}}
                  value={lang?.code}
                >
                  {lang?.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </CustomBadge>
    </Stack>
  )
}

export default LanguageSelector

const highlightAnimation = keyframes({
  to: {
    backgroundPosition: '200% center',
  },
})

const useStyles = makeStyles()((theme: Theme, props) => ({
  glowingBox: {
    padding: '2px',
    borderRadius: 8,
    backgroundSize: '200% auto',
    animation: `${highlightAnimation} 2s linear infinite`,
  },
  glowingInner: {
    paddingTop: '2px',
    paddingBottom: '2px',
    paddingRight: '6px',
    paddingLeft: '6px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: 6,
  },
  languageIcon: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}))
